"use client";

import Image from "next/image";
import type React from "react";

interface CaptionExample {
  imageSrc: string;
  imageAlt: string;
  useCase: string;
  length: "short" | "normal" | "long";
  caption: string;
}

// Examples showing all three caption lengths for different use cases
const captionExamples: CaptionExample[] = [
  {
    imageSrc: "/images/nike.png",
    imageAlt: "Nike shoe product",
    useCase: "Product Cataloging",
    length: "short",
    caption: "White Nike Air Force 1 high-top sneaker with classic design.",
  },
  {
    imageSrc: "/images/dog_caption_example.png",
    imageAlt: "Dog with frisbee",
    useCase: "Accessibility Alt-Text",
    length: "normal",
    caption: "A brown dog wearing a blue harness and leash holds a green and blue frisbee in its mouth while standing on a gray sidewalk. A bouquet of yellow flowers is visible nearby, adding a splash of color to the scene.",
  },
  {
    imageSrc: "/images/solutions/wildlife-detection/moose_example.jpg",
    imageAlt: "Moose trail camera",
    useCase: "Content Indexing",
    length: "short",
    caption: "A moose with large antlers walks on a dirt path in a forest.",
  },
  {
    imageSrc: "/images/busy_airport.jpeg",
    imageAlt: "Airport terminal",
    useCase: "Synthetic Data",
    length: "long",
    caption: "A bustling airport terminal interior captured from an elevated perspective, showing travelers navigating through a spacious concourse. The architecture features a dramatic vaulted ceiling with exposed structural beams and large glass panels allowing natural light to flood the space. Multiple departure gates line both sides of the terminal. Travelers of various ages pull rolling luggage while others rest on rows of connected seating. The polished floor reflects the overhead lighting.",
  },
];

const CaptionCard: React.FC<{ example: CaptionExample; priority?: boolean }> = ({
  example,
  priority = false,
}) => {
  return (
    <div className="group relative w-full overflow-hidden rounded-2xl aspect-[4/3]">
      {/* Background Image */}
      <Image
        fill
        src={example.imageSrc}
        alt={example.imageAlt}
        sizes="(min-width: 1024px) 50vw, 100vw"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        priority={priority}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

      {/* Use case label */}
      <div className="absolute top-4 left-4">
        <span className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-white bg-white/15 backdrop-blur-md rounded-full border border-white/20 shadow-lg">
          {example.useCase}
        </span>
      </div>

      {/* Caption container */}
      <div className="absolute bottom-4 left-4 right-4">
        <div className="bg-white/15 backdrop-blur-md rounded-lg border border-white/25 px-3 py-2.5 shadow-lg">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-amber-300 text-[10px] font-semibold uppercase tracking-wider">Caption</span>
            <span className="text-white/50 text-[10px] uppercase tracking-wider">• {example.length}</span>
          </div>
          <p className="text-white font-medium text-sm leading-relaxed line-clamp-3">
            {example.caption}
          </p>
        </div>
      </div>
    </div>
  );
};

const Demos: React.FC = () => {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
      {captionExamples.map((example, index) => (
        <CaptionCard
          key={index}
          example={example}
          priority={index === 0}
        />
      ))}
    </div>
  );
};

export { Demos };
